/*
 * @Description: 东南亚驿站外场（越南、菲律宾等）对接沙滩说
 * @Author: gaolei
 * @Date: 2025-06-24
 * @LastEditors: gaolei
 */

import { useAuthStore } from '@/stores/login'
import dsBridge from 'dsbridge'
import { loading } from '@/utils/loading'
import axios from 'axios'
import i18n from '@/locales'
import { v4 as uuidv4 } from 'uuid'
import CryptoJS from 'crypto-js'
import router from '@/router'
import { useUsersStore } from '@/stores/users'
import { getPlatform } from './Device'

// 判断是否是东南亚驿站外场APP
export const isSandsTalkSEAsiaOutfieldApp = window.navigator.userAgent.indexOf('seAsiaOutfield') > -1
export const AppVersion = window.navigator.userAgent.indexOf('AppVersion') > -1

// 在项目中创建一个全局声明文件（如：global.d.ts）
declare global {
  interface Window {
    jtJsBridge?: {
      jtH5CallNativeApi: (request: any, callback: (responseData: any) => void) => void
    }
  }
}

const callNative = async (request: { method: string; data?: any }): Promise<any> => {
  return new Promise((resolve) => {
    dsBridge.call('jtH5CallNativeApi', request, (responseData) => {
      if (responseData && typeof responseData === 'string') {
        try {
          const parsedResponse = JSON.parse(responseData)
          resolve(parsedResponse)
        } catch {
          resolve(responseData)
        }
      } else {
        resolve(responseData)
      }
    })
  })
}

const callNative2 = async (request: { method: string; data?: any }): Promise<any> => {
  return new Promise((resolve) => {
    dsBridge.call('jtH5CallNativeApi', request, (responseData) => {
      resolve(responseData)
    })
  })
}

// 获取签名
function getSign(params: any, secret: any, timestamp: any) {
  const paramsJson = params ? JSON.stringify(params, Object.keys(params).sort()) : 'N_V'
  const stringToSign = `${paramsJson}\n${timestamp}\n${secret}`

  const hmac = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA256, CryptoJS.enc.Utf8.parse(stringToSign))
  const signData = hmac.finalize(CryptoJS.lib.WordArray.create([]))

  return CryptoJS.enc.Base64.stringify(signData)
}

// 东南亚驿站外场配置表
const SE_ASIA_OUTFIELD_CONFIG: Record<string, {
  appId: string
  signSecret: string
  defaultLang: string
  identifierType: string
}> = {
  // 越南驿站
  'vn_yoyi_app': {
    appId: 'vn_yoyi_app',
    signSecret: 'ZIAHidtxqWqlekiz',
    defaultLang: 'vi',
    identifierType: 'empNo'
  },
  // 菲律宾驿站
  'ph_yoyi_app': {
    appId: 'ph_yoyi_app',
    signSecret: 'cktdCahIizybP4g9',
    defaultLang: 'en-us',
    identifierType: 'empNo'
  }
}

// 东南亚驿站外场免登登录
export const sandsTalkSEAsiaOutfieldLogin = async () => {
  loading.show()
  try {
    const u = navigator.userAgent
    const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1

    let getUserInfor = {} as {
      source?: string
      sourceType?: string
      identifierType?: string
      identifierValue?: string
      empNo?: string
      lang?: string
    }

    if (isAndroid) {
      // Android 平台使用 dsBridge 直接调用
      const res = await dsBridge.call('SandsTalkUserInfoChannel')
      getUserInfor = JSON.parse(res)
    } else {
      // iOS 平台使用 callNative 方法
      const request = { method: 'SandsTalkUserInfoChannel' }
      const res = await callNative2(request)
      let parsedRes
      try {
        parsedRes = typeof res === 'string' ? JSON.parse(res) : res
      } catch {
        parsedRes = res
      }
      getUserInfor = parsedRes.data || parsedRes
    }

    // 存储原生给的用户信息数据到 localStorage
    localStorage.setItem('SandsTalkUserInfo', JSON.stringify(getUserInfor))

    const source = getUserInfor.source || ''
    const config = SE_ASIA_OUTFIELD_CONFIG[source]

    if (!config) {
      loading.hide()
      seAsiaOutfieldShowToast('未知的外场来源')
      seAsiaOutfieldCloseWebview()
      return
    }

    const appId = config.appId
    const signSecret = config.signSecret
    const language = getUserInfor.lang || config.defaultLang
    // 优先使用原生返回的 identifierType，如果为空则使用配置中的默认值
    const identifierType = getUserInfor.identifierType ? getUserInfor.identifierType : config.identifierType
    const identifierValue = getUserInfor.empNo ? getUserInfor.empNo : ''

    localStorage.setItem('lang', language)

    const timestamp = Date.now()
    const noce = uuidv4()

    const params = {
      appId,
      identifierType,
      identifierValue,
      noce,
      timestamp
    }

    const sign = getSign(params, signSecret, timestamp)

    const finalRequestData = {
      ...params,
      sign
    }

    const { data } = await axios.post(
      `${process.env.VUE_APP_API_URL}/sands-user/api/v1/auth/authorization-token`,
      JSON.stringify(finalRequestData),
      {
        headers: {
          'Content-Type': 'application/json',
          'accept-language': language,
          'Common-Platform': getPlatform()
        }
      }
    )

    if (data.code === 1) {
      loading.hide()
      const authStore = useAuthStore()
      authStore.setAccessToken(data.data.access_token)
      localStorage.setItem('sandsTalkToken', data.data.access_token)
      localStorage.setItem('sandsTalkTenantId', data.data.tenantId || '1000')
      const usersStore = useUsersStore()
      usersStore.setCollegeId(data.data.collegeId)

      // 根据 sourceType 跳转不同页面
      if (getUserInfor.sourceType === 'courseList') {
        router.replace('/course')
      } else if (getUserInfor.sourceType === 'Training') {
        router.replace('/myTraining')
      } else if (getUserInfor.sourceType === 'studyTask') {
        router.replace('/task')
      } else {
        router.replace('/')
      }
      return data.data.access_token
    } else {
      handleAuthError(data.msg)
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      router.replace({
        name: 'ErrorPage',
        query: {
          info: i18n.global.t('当前使用人数过多,请稍后再试'),
          imgSrc: 'error_cont.png',
          showRetry: 'true',
          retryText: i18n.global.t('重试'),
          redirect: window.location.href
        }
      })
      loading.hide()
      return
    }
    handleAuthError(error)
  }
}

// 统一处理鉴权失败
const handleAuthError = (message: any) => {
  const authStore = useAuthStore()
  authStore.setAccessToken('')
  localStorage.removeItem('sandsTalkToken')
  loading.hide()
  seAsiaOutfieldShowToast(String(message))
  seAsiaOutfieldCloseWebview()
}

// ========== 东南亚驿站 Bridge 方法 ==========

/**
 * @Description 关闭Webview（H5调用原生）
 */
export const seAsiaOutfieldCloseWebview = async () => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1

  if (isAndroid) {
    dsBridge.call('finish')
  } else {
    if (AppVersion) {
      const request = { method: 'FinishPageChannel' }
      await callNative(request)
    } else {
      dsBridge.call('finish')
    }
  }
}

/**
 * @Description 显示Toast提示（H5调用原生）
 */
export const seAsiaOutfieldShowToast = async (msg: string) => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    await dsBridge.call('sandsTalkShowToast', msg)
  } else {
    const request = { method: 'sandsTalkShowToast', msg }
    await callNative(request)
  }
}

/**
 * @Description 下载文件（H5调用原生）
 */
export const seAsiaOutfieldDownloadFile = async (url: string) => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    await dsBridge.call('downloadStudyFile', url)
  } else {
    if (AppVersion) {
      const request = { method: 'downloadStudyFile', url }
      await callNative(request)
    } else {
      await dsBridge.call('downloadStudyFile', url)
    }
  }
}

/**
 * @Description 打开新页面（H5调用原生）
 */
export const seAsiaOutfieldOpenUrl = async (url: string) => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    dsBridge.call('goToNativeLoadUrl', url)
  } else {
    const request = { method: 'goToNativeLoadUrl', url }
    await callNative(request)
  }
}

/**
 * @Description 设置原生页面标题（H5调用原生）
 */
export const seAsiaOutfieldSetTitle = async (title: string) => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    await dsBridge.call('outFieldSetTitle', title)
  } else {
    const request = { method: 'outFieldSetTitle', data: title }
    await callNative(request)
  }
}

/**
 * @Description 视频全屏状态切换（H5调用原生）
 */
export const seAsiaOutfieldSetHoriVerti = async (isFull: boolean) => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    await dsBridge.call('outFieldSetHoriVerti', isFull)
  } else {
    const request = { method: 'outFieldSetHoriVerti', data: isFull }
    await callNative(request)
  }
}

// 添加东南亚驿站返回处理
export const registerSEAsiaOutfieldPrevious = () => {
  dsBridge.register('previous', function () {
    const currentRoute = router.currentRoute.value
    const userInfoData = JSON.parse(localStorage.getItem('SandsTalkUserInfo') || '{}')
    if (currentRoute.path === '/' || currentRoute.path === '/myTraining' || userInfoData.sourceType === 'training') {
      seAsiaOutfieldCloseWebview()
    } else {
      router.go(-1)
    }
  })
}
